import { FunctionComponent, h, render, Fragment } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { getApi } from '../../../src/ts/libs/getApi';
import { getQueryString, createQueryString, filterQueryString } from '../../../src/ts/libs/queryStrings';
import { mergeArraysUnique } from '../../../src/ts/libs/mergeArraysUnique';
import { useFetch } from '../hooks/useFetch';
import { currency } from '../../../src/ts/libs/currency';
import { ActionButton } from '../action-button/ActionButton';
import { KeyValueMapping, Item, MyOrdersData, Orders, SearchOrderProps } from '../context/Orders';
import { InfoBlock } from '../product-list-wrap/product-list-wrap';
import { LoadMore } from '../common/LoadMore';

const canAddToBasket = (sellingUnitValue) => sellingUnitValue != 1 && sellingUnitValue <= 2 && sellingUnitValue != -1;

interface OrderListTableProps {}
const OrderListToolBar: FunctionComponent<OrderListTableProps> = () => {
  const { orderStatuses, search, handleFreshSearch } = useContext(Orders);

  const handleChangeActive = (e: Event, data) => {
    e.preventDefault();
    handleFreshSearch(data);
  };
  const handleChangeType = (e) => {
    e.preventDefault();
    handleFreshSearch({ orderStatus: e.target.value });
  };

  return (
    <div className="band band--muted">
      <div className="band__inner">
        <div className="band__content">
          <div className="layout-50-50 band--muted  ">
            <div className="layout-50-50__column">
              <div className="nav-links nav-links__trade">
                <ul className="nav-links__items">
                  <li className={`nav-links__item  ${search.active ? 'nav-links__item--active' : ''}`}>
                    <a
                      href="#"
                      className="nav-links__item__action"
                      onClick={(e) => handleChangeActive(e, { active: true })}
                    >
                      Open Orders
                    </a>
                  </li>
                  <li className={`nav-links__item  ${!search.active ? 'nav-links__item--active' : ''}`}>
                    <a
                      href="#"
                      className="nav-links__item__action"
                      onClick={(e) => handleChangeActive(e, { active: false })}
                    >
                      Order History
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="layout-50-50__column">
              <div className="orders-table-dropdown">
                <div className="form-field form-field__label-inline  ">
                  <label className="form-field__label" htmlFor="orderType">
                    Order Type
                  </label>
                  <select name="orderTypeStatus" id="orderTypeStatus" className="select" onChange={handleChangeType}>
                    <option value="ALL">Show all</option>
                    {orderStatuses?.length >= 1 &&
                      orderStatuses.map((status) => (
                        <option value={status.Value} selected={status.Value === search.orderStatus}>
                          {status.Key}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface OrderRecordProps {
  item: Item;
  images: KeyValueMapping[];
  availableSellingUnits: KeyValueMapping[];
}
const buildFullAddress = (address) => {
  const [_, ...addresParts] = Object.values(address);
  const fullAddress = addresParts
    .filter((addressPart) => !!addressPart)
    .map((addressPart) => (typeof addressPart === 'string' ? addressPart.trim() : addressPart?.Name.trim()))
    .join(', ');
  return fullAddress;
};
const OrderRecord: FunctionComponent<OrderRecordProps> = ({ item, images, availableSellingUnits }) => {
  const { sellingUnitMapping } = useContext(Orders);
  const [showDetails, setShowDetails] = useState(false);

  // Filter for avaiable products in availableSellingUnits
  const fullAddress = item.Address ? buildFullAddress(item.Address) : '';

  const reorderableProducts = item.SalesOrder.Items.filter((product) => {
    const sellingUnitValue = +availableSellingUnits.find((su) => su.Key === product.Product.ID.toString())?.Value;
    return (
      canAddToBasket(sellingUnitValue) &&
      product.Product.Code.trim() !== 'SOS' &&
      availableSellingUnits.find((sellingUnit) => sellingUnit.Key === product.Product.ID.toString())
    );
  }).map((product) => ({
    productCode: product.Product.ID,
    quantity: product.Quantity,
    sellingUnit: sellingUnitMapping.find((sellingUnit) => sellingUnit.Key === product.Product.ID.toString())?.Value,
  }));

  // availableSellingUnits.find((su) => su.Key === product.Product.ID.toString())?.Value;
  return (
    <div className="trade-account-single-order">
      <div className="trade-order__header">
        <div className="trade-order__headings">
          <div className="trade-order__header--order-number">
            <p>
              <strong>Order number</strong>
              {item.SalesOrder.Number}
            </p>
          </div>
          <div className="trade-order__header--po-number" />
          <div className="trade-order__header--order-date">
            <p>
              <strong>Order date</strong>
              {item.SalesOrder.Date}
            </p>
          </div>
          <div className="trade-order__header--order-status">
            <p>
              <strong>Order status</strong>
              {item.SalesOrder.WorkflowStatus.Description}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="trade-order__header--show-details"
          onClick={() => setShowDetails((dets) => !dets)}
        >
          <span className="inner-label">{showDetails ? 'Hide order details' : 'Show order details'}</span>
          <svg aria-hidden="true" width={7} height={8} xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.49 3.575L2.09.176a.597.597 0 00-.846 0L.679.741a.597.597 0 000 .847L3.085 4 .676 6.41a.597.597 0 000 .846l.565.568a.597.597 0 00.847 0l3.4-3.4a.598.598 0 00.002-.849z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
        </button>
      </div>
      <div className="trade-order-table">
        <div className={`trade-order-table__details ${showDetails ? 'show' : ''}`}>
          <div className="layout-50-50">
            <div className="layout-50-50__column">
              <div className="panel panel--no-padding panel--space-bottom  ">
                <h3 className="panel__heading">Delivering to</h3>
                <div className="panel panel--grey panel--min-height  ">
                  <strong>{item?.SalesOrder?.Customer?.Name}</strong>
                  <p>{fullAddress}</p>
                </div>
              </div>
            </div>
            <div className="layout-50-50__column">
              <div className="panel panel--no-padding panel--space-bottom  ">
                <h3 className="panel__heading">Payment method</h3>
                <div className="panel panel--grey panel--min-height  ">
                  <p>{item.SalesOrder.OrderType.Description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="trade-order-table__header">
          <p>Description</p>
          <div className="trade-order-table__header__details">
            <span />
            <p>Quantity</p>
            <p>Selling unit</p>
            <p>Unit price</p>
            <p>Subtotal</p>
          </div>
        </div>
        {item.SalesOrder.Items.map((product) => {
          const imgSrc = images.find((img) => img.Key === product.Product.ID.toString())?.Value;
          const sellingUnitValue = +availableSellingUnits.find((su) => su.Key === product.Product.ID.toString())?.Value;
          const isSOS = product.Product.Code.trim() === 'SOS';

          return (
            <div className="trade-order-table__row" data-suv={sellingUnitValue}>
              <div className="trade-order-table__row-in">
                <img src={imgSrc || '/assets/img/no-photo-product.png'} alt={product.Product.Description} />
                <div className="trade-order-table__row__details">
                  <h4>
                    <strong>{product.Product.Description}</strong>
                    <span>
                      <strong>Code: </strong> {product.Product.Code}
                    </span>
                  </h4>
                  <p>
                    <strong>Quantity: </strong> {product.Quantity}
                  </p>
                  <p>
                    <strong>Selling unit:</strong> {product?.SellingUnits?.Description}
                  </p>
                  <p>
                    <strong>Unit Price:</strong> {currency(product.NetPrice)}
                  </p>
                  <p>
                    <strong>Subtotal:</strong> {currency(product.NetAmount)}
                  </p>
                  {!isSOS && canAddToBasket(sellingUnitValue) && (
                    <ActionButton
                      label="Add to order"
                      icon="cart"
                      endpoint="addToBasket"
                      payload={{
                        productCode: product.Product.ID,
                        quantity: product.Quantity,
                        sellingUnit: sellingUnitMapping.find(
                          (sellingUnit) => sellingUnit.Key === product.Product.ID.toString()
                        )?.Value,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="panel trade-order-table__summary panel--grey ">
          <div className="trade-order-table__summary__details">
            <p className="trade-order-table__summary__info">
              <strong>Total (include VAT and Delivery)</strong>
            </p>
            <p className="trade-order-table__summary__total">
              <strong className="trade-order-table__summary__total__amount">
                {currency(item.SalesOrder.GrossAmount)}
              </strong>
            </p>
          </div>
        </div>
        <div className="trade-order-table__footer">
          <div className="d-flex"></div>
          <div>
            {reorderableProducts?.length >= 1 && (
              <ActionButton
                label="Reorder"
                icon="reorder"
                endpoint="addToBasketMultiple"
                payload={{ basketItems: reorderableProducts }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoaderCard = () => <div class="product-row-loader"> </div>;

const OrderList = () => {
  const firstLoad = useRef(true);

  const allowed = ['page', 'search', 'order', 'orderStatus', 'active'];
  const searchRaw = window.location.search ? getQueryString(window.location.search) : {};

  const filteredSearch: any = filterQueryString(searchRaw, allowed);
  const baseSearch = {
    page: 1,
    limit: 8,
    filters: [],
    order: 'alphabeticallyAscending',
    active: true,
    orderStatus: 'ALL',
    ...filteredSearch,
  };
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState<SearchOrderProps>(baseSearch);
  const [orderStatuses, setOrderStatuses] = useState<KeyValueMapping[]>([]);
  const [stockingStatusMapping, setStockingStatusMapping] = useState<KeyValueMapping[]>([]);
  const [sellingUnitMapping, setSellingUnitMapping] = useState<KeyValueMapping[]>([]);
  const [imageMapping, setImageMapping] = useState<KeyValueMapping[]>([]);
  const { response, error, loading, fetchData, called } = useFetch<MyOrdersData>(`${getApi('myOrders')}`, {});

  const handleUpdateMerged = (data) => {
    if (data?.items?.length >= 1) {
      setItems([...items, ...data.items]);
    }
    setOrderStatuses(mergeArraysUnique(data.OrderStatuses, orderStatuses));
    setStockingStatusMapping(mergeArraysUnique(data.StockingStatusMapping, stockingStatusMapping));
    setSellingUnitMapping(mergeArraysUnique(data.SellingUnitMapping, sellingUnitMapping));
    setImageMapping(mergeArraysUnique(data.ImageMapping, imageMapping));
  };
  const handleUpdate = (data) => {
    setItems(data.items);
    setOrderStatuses(data.OrderStatuses);
    setStockingStatusMapping(data.StockingStatusMapping);
    setSellingUnitMapping(data.SellingUnitMapping);
    setImageMapping(data.ImageMapping);
  };
  useEffect(() => {
    (async () => {
      const firstLoadResponse = await fetchData({
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...search,
          page: 1,
          limit: firstLoad.current && +filteredSearch.page > 1 ? 8 * +filteredSearch.page : 8,
        }),
      });
      firstLoad.current = false;
      handleUpdate(firstLoadResponse.data);
    })();
  }, []);

  useEffect(() => {
    const newQueryString = createQueryString(filterQueryString(search, allowed));

    if (JSON.stringify(search) !== JSON.stringify(baseSearch)) {
      history.pushState(null, null, `?${newQueryString}`);
    }
  }, [search]);

  const handleFreshSearch = async (searchdata: Partial<SearchOrderProps>) => {
    handleUpdate([]);
    const newSearch = { ...search, ...searchdata };
    setSearch(newSearch);

    const fullUpdate = await fetchData({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(newSearch),
    });

    handleUpdate(fullUpdate.data);

    return fullUpdate;
  };

  const handleGetMore = async (e) => {
    e.preventDefault();
    const newSearch = { ...search, page: +search.page + 1 };

    setSearch(newSearch);

    const pageUpate = await fetchData({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(newSearch),
    });

    handleUpdateMerged(pageUpate.data);

    return pageUpate;
  };
  return (
    <Orders.Provider
      value={{
        items,
        setItems,
        search,
        setSearch,
        orderStatuses,
        setOrderStatuses,
        stockingStatusMapping,
        setStockingStatusMapping,
        sellingUnitMapping,
        setSellingUnitMapping,
        imageMapping,
        setImageMapping,
        handleFreshSearch,
      }}
    >
      <OrderListToolBar />
      <div className="band band--muted">
        <div className="band__inner">
          <div className="band__content">
            {items?.map((item) => (
              <OrderRecord item={item} images={imageMapping} availableSellingUnits={stockingStatusMapping} />
            ))}
            {loading && [...Array(2)].map((_, index) => <LoaderCard key={`loader${index}`} />)}

            {called && !loading && error && (
              <InfoBlock message="Sorry there was an error loading the product results" error />
            )}

            {called && !loading && !error && response?.items?.length === 0 && <InfoBlock message="No orders found" />}

            <LoadMore count={items?.length || 0} response={response} handleGetMore={handleGetMore} loading={loading} />
          </div>
        </div>
      </div>

      {/* <pre>
        <code>{JSON.stringify(response, null, 2)}</code>
      </pre> */}
    </Orders.Provider>
  );
};

try {
  const domContainer = document.querySelector('#my-orders');
  if (domContainer) {
    const OrderListInner = domContainer.querySelector('.my-orders');
    render(<OrderList />, domContainer, OrderListInner);
  }
} catch (e) {
  console.warn(e);
}
